import {
  ConfigEventFilterKey,
  ConfigEventFilterSet,
  ConfigFilterAtomValues,
  ConfigFilterKey,
  ConfigFilterSet,
  ConfigGameFilterKey,
  ConfigGameFilterSet,
  ConfigPlayerFilterKey,
  ConfigPlayerFilterSet,
  FilterSetType,
  Gender,
  KeysToCamelCase,
  NavigationLinkMapping,
  Position,
  PositionGroup,
  TitleSettings,
  UserConfig,
  UserConfigDefinition,
  UserConfigFilter,
  UserConfigFilterDefinition,
} from '@statsbomb/parachute-types';
import { PrimitiveAtom } from 'jotai';
import { Entity } from './entity';
import { RadarDataFormat } from './radar';
import { DashboardWidgetConfigVis } from './widget';

export type {
  ConfigEventFilterKey,
  ConfigEventFilterSet,
  ConfigFilterAtomValues,
  ConfigFilterKey,
  ConfigFilterSet,
  ConfigGameFilterKey,
  ConfigGameFilterSet,
  ConfigPlayerFilterKey,
  ConfigPlayerFilterSet,
  TitleSettings,
  UserConfigFilter,
  UserConfigFilterDefinition,
};

export type UserConfigCamelCase = KeysToCamelCase<UserConfig>;

type UserConfigWithoutDefinition = KeysToCamelCase<Omit<UserConfig, 'definition'>>;

export interface UserConfigDashboardTemplateDefinition extends UserConfigWithoutDefinition {
  definition: {
    entity: string;
    titleSettings: TitleSettings;
    widgetIds: string[];
  };
}

export interface UserConfigWithTitle extends UserConfigWithoutDefinition {
  definition: {
    titleSettings: TitleSettings;
  };
}

export interface UserConfigWidgetDefinition extends UserConfigWithoutDefinition {
  definition: DashboardWidgetConfigVis;
}

export interface UserConfigTableColumns {
  tableId: string;
  selectedColumns: string[];
}

export interface UserConfigTableColumnsDefinition extends UserConfigWithoutDefinition {
  definition: UserConfigTableColumns;
}

export interface UserConfigSettingsDefinition {
  team: KeysToCamelCase<NavigationLinkMapping>;
  gender?: Gender;
  locale?: string;
  radar?: {
    dataFormat?: RadarDataFormat;
  };
}

export interface UserConfigSettings extends UserConfigWithoutDefinition {
  definition: UserConfigSettingsDefinition;
}

export interface RadarTemplateDefinition {
  entity: Entity;
  gender: Gender;
  positionGroup: PositionGroup | null;
  positions?: Position[] | null;
  titleSettings: TitleSettings;
  metrics: {
    key: string;
    keyNormalised: string;
    min: number;
    max: number;
    default: boolean;
  }[];
}

export interface RawUserConfigRadarTemplate extends UserConfig {
  definition: RadarTemplateDefinition;
}
export interface UserConfigRadarTemplate extends UserConfigWithoutDefinition {
  definition: RadarTemplateDefinition;
}

// The string values here are persisted per-user as onboarding dismissal records. Never delete or
// repurpose an existing key/value: reusing a value for a new modal means any user who already
// dismissed the previous modal with that value will never see the new one. Only ever add new members.
export enum OnboardingItemKey {
  TRANSLATIONS = 'translations',
  PLAYER_FEATURES = 'playerFeatures',
  SCOUT_FEATURES = 'scoutFeatures',
  TEAM_EXPLORE = 'teamExplore',
  EVENT_EXPLORE = 'eventExplore',
  ATTRIBUTES_FILTER = 'attributeFilters',
  THREE_SIXTY = 'threeSixty',
  TEAM_PAGES = 'teamPages',
  RADAR_TEMPLATES = 'radarTemplates',
  RECRUITMENT_LISTS = 'recruitmentLists',
  COMPARISON_RADAR = 'comparisonRadar',
  APPEARANCE_SETTINGS = 'appearanceSettings',
  CONTEXTUAL_RANKINGS = 'contextualRankings',
  RADAR_DATA_FORMAT_SETTINGS = 'radarDataFormatSettings',
  SUMMARY_BETA = 'summaryBeta',
  AI_SUMMARY_PLAYER = 'aiSummaryPlayer',
  AI_SUMMARY_SCOUT = 'aiSummaryScout',
  PITCH_VIS_OBV_COLOURING = 'pitchVisObvColouring',
  RADAR_DISTRIBUTION_VIEW = 'radarDistributionView',
}

export interface UserConfigOnboardingDefinition extends UserConfigWithoutDefinition {
  definition: Partial<Record<OnboardingItemKey, boolean>>;
}

export const UserConfigType = {
  SETTINGS: 'settings',
  TABLE_COLUMNS: 'tableColumns',
  ONBOARDING: 'firstTimeModal',
  RADAR_TEMPLATE: 'radarTemplate',
  ...FilterSetType,
} as const;

// Value and type intentionally share a name — standard TS pattern for typed const enums.
// eslint-disable-next-line no-redeclare
export type UserConfigType = (typeof UserConfigType)[keyof typeof UserConfigType];

export interface UserConfigPutMutationParams {
  configId?: string;
  configType: UserConfigType;
  configDefinition: UserConfigDefinition;
}

type FilterAtomMapping<T extends ConfigFilterKey> = Record<T, PrimitiveAtom<any>>;

export type ConfigGameFilterAtoms = FilterAtomMapping<ConfigGameFilterKey>;

export type ConfigEventFilterAtoms = FilterAtomMapping<ConfigEventFilterKey>;

export type ConfigPlayerFilterAtoms = FilterAtomMapping<ConfigPlayerFilterKey>;

export type ConfigFilterAtoms = FilterAtomMapping<ConfigFilterKey>;

type AtomHydrateCallbackMapping<T extends ConfigFilterKey> = Record<T, (value: any) => any>;

export type ConfigGameFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigGameFilterKey>;

export type ConfigEventFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigEventFilterKey>;

export type ConfigPlayerFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigPlayerFilterKey>;

export type ConfigFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigFilterKey>;

export enum ConfigDisplaySettingKey {
  RADAR_TEMPLATE_ID = 'radarTemplateId',
  PITCH_ORIENTATION = 'pitchOrientation',
  PITCH_CROP = 'pitchCrop',
  SHOW_ARROWS = 'showArrows',
  IS_START_PLACEMENT = 'isStartPlacement',
  PITCH_MARKER_COLOURING_MODE = 'pitchMarkerColouringMode',
  ORDER_BY = 'orderBy',
  ORDER_DIRECTION = 'orderDirection',
  BAR_CHART_ORIENTATION = 'barChartOrientation',
}

export type ConfigDisplaySettings = Record<ConfigDisplaySettingKey, any>;

export type ConfigDisplaySettingAtoms = Record<ConfigDisplaySettingKey, PrimitiveAtom<any>>;

export type ConfigDisplaySettingAtomHydrateCallbacks = Record<ConfigDisplaySettingKey, (value: any) => any>;

export type AtomsToHydrate = [PrimitiveAtom<unknown>, unknown][];

export enum UserConfigActions {
  CREATE = 'create',
  DUPLICATE = 'duplicate',
  RENAME = 'rename',
  DELETE = 'delete',
  EDIT = 'edit',
  SHARE = 'share',
}
